<template>
  <CModal :show="show" :closeOnBackdrop="false" :centered="true" size="xl">
    <template #header>
      <h5 class="modal-title">{{ title }}</h5>
    </template>

    <p v-if="desc">
      {{ $t("family.dashboard.zip_modal.text") }}
    </p>

    <p>
      {{ $t("models.family_address.geocodingHelpText") }}
    </p>

    <CAlert v-if="error.hasGeneralErrors()" color="danger" class="mb-3">
      {{ error.general().join(" ") }}
    </CAlert>

    <form action="" @submit.prevent="onSubmit">
      <CInput
        v-model="form.to"
        :label="$t('models.family_address.to')"
        type="text"
        :isValid="error.isValidField('to')"
        :invalidFeedback="error.fieldError('to')"
        required
      />

      <CInput
        v-model="form.address"
        :label="$t('models.family_address.address')"
        type="text"
        :isValid="error.isValidField('address')"
        :invalidFeedback="error.fieldError('address')"
        required
        @input="onFormChange"
      />

      <CInput
        v-model="form.city"
        :label="$t('models.family_address.city')"
        type="text"
        :isValid="error.isValidField('city')"
        :invalidFeedback="error.fieldError('city')"
        required
        @input="onFormChange"
      />

      <CSelect
        :label="$t('models.family_address.country')"
        :options="countryOptions"
        :value.sync="form.country"
        :isValid="error.isValidField('country')"
        :invalidFeedback="error.fieldError('country')"
        custom
        @change="onFormChange"
      />

      <div class="form-group">
        <label>{{ $t("models.family_address.postal_code") }}</label>
        <v-select
          class="bg-white"
          label="code"
          v-model="form.postal_code"
          :options="postalCodeOptions"
          @search="fetchPostalCodeOptions"
          @change="onFormChange"
        />
        <div
          v-if="error.hasFieldsError('postal_code')"
          class="invalid-feedback d-block"
        >
          {{ error.fieldError("postal_code") }}
        </div>
      </div>

      <CInputCheckbox
        v-if="this.$store.state.connections.current.zip_code"
        :label="$t('models.family_address.default_ship')"
        :checked.sync="form.default_ship"
        :isValid="error.isValidField('default_ship')"
        :invalidFeedback="error.fieldError('default_ship')"
        custom
      />

      <CInputCheckbox
        v-if="this.$store.state.connections.current.zip_code"
        :label="$t('models.family_address.default_bill')"
        :checked.sync="form.default_bill"
        :isValid="error.isValidField('default_bill')"
        :invalidFeedback="error.fieldError('default_bill')"
        custom
      />
    </form>

    <div
      v-if="geocodingFailed || geoCodingConfirm"
      class="rounded-top overflow-hidden"
    >
      <CAlert color="warning" class="mb-2 mt-4" v-if="geocodingFailed">
        {{ $t("models.family_address.geocodingFailedMsg") }}
      </CAlert>
      <CAlert color="warning" class="mb-2 mt-4" v-if="geoCodingConfirm">
        {{ $t("models.family_address.geocodingConfirmMsg") }}
      </CAlert>
      <LMap
        ref="map"
        :zoom="geocodingFailed ? 8 : 18"
        :options="{
          zoomControl: true,
          scrollWheelZoom: true,
          doubleClickZoom: true,
          touchZoom: true,
          dragging: true,
        }"
        :center="
          geocodingFailed ? [46.8007, 8.2227] : [form.latitude, form.longitude]
        "
        style="height: 350px; width: 100%"
        @ready="setupMapClickHandler"
      >
        <LTileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        <LMarker
          v-if="geoCodingConfirm"
          :lat-lng="[form.latitude, form.longitude]"
          :icon="customMarkerIcon"
        />
      </LMap>
    </div>

    <template #footer>
      <CButton
        color="primary"
        variant="outline"
        @click.prevent="closeModal"
        v-if="$store.state.connections.current.zip_code"
        >{{ $t("common.editor.cancel") }}</CButton
      >
      <vue-ladda
        :loading="loading"
        data-style="zoom-in"
        button-class="btn btn-primary px-4"
        :disabled="
          form.address &&
          form.city &&
          form.ppostal_code &&
          form.country &&
          form.to
        "
        @click.prevent="onSubmit"
      >
        <span v-if="!form.latitude && !form.longitude">
          {{ $t("common.search_label") }}
        </span>
        <span v-else>
          {{ $t("common.editor.confirm") }}
        </span></vue-ladda
      >
    </template>
  </CModal>
</template>

<script>
import get from "lodash/get";
import debounce from "lodash/debounce";
import pick from "lodash/pick";
import errorResponse from "../../../helpers/error";
import {
  getNationsActiveOptions,
  getPostalCodeOptions,
} from "../../../helpers/options";
import vSelect from "vue-select";
import EventBus from "../../../helpers/EventBus";
import { apiPatch, apiPost } from "../../../helpers/apiHelper";
import { geocodeAddress } from "../../../helpers/common";
import { LMap, LTileLayer, LMarker, LIcon } from "vue2-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { CIcon } from "@coreui/icons-vue";

const formDefaults = () => {
  return {
    id: null,
    to: "",
    address: "",
    city: "",
    postal_code: null,
    country: "",
    default_ship: false,
    default_bill: false,
    latitude: null,
    longitude: null,
  };
};

export default {
  name: "AddressesModal",
  components: {
    vSelect,
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
    CIcon,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    values: {
      type: Object,
      default: null,
    },
    desc: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      initialized: false,
      loading: false,
      error: errorResponse(),
      form: formDefaults(),
      countryOptions: [],
      postalCodeOptions: [],
      geocodingFailed: false,
      geoCodingConfirm: false,
      customMarkerIcon: L.icon({
        iconUrl: "/loonity/img/locationpin.svg",
        iconSize: [32, 37],
        iconAnchor: [16, 37],
      }),
      iconSize: 64,
      marker: null,
    };
  },

  watch: {
    show(value) {
      if (value === true) {
        this.init();
      }
    },
    "form.country": function () {
      if (this.initialized) {
        this.form.postal_code = null;
      }
    },
  },

  mounted() {
    if (this.show) {
      this.init();
    }
  },

  methods: {
    init() {
      this.initialized = false;
      this.form = this.isEdit ? { ...this.values } : formDefaults();
      this.form.latitude = null;
      this.form.longitude = null;
      this.geocodingFailed = false;
      this.geoCodingConfirm = false;
      if (this.marker) {
        this.marker = null;
      }
      this.getCountryOptions();
      this.$nextTick(() => {
        this.initialized = true;
      });
      if (!this.$store.state.connections.current.zip_code) {
        this.form.default_ship = true;
        this.form.default_bill = true;
      }
    },

    getCountryOptions() {
      getNationsActiveOptions()
        .then((options) => {
          this.countryOptions = [
            {
              value: null,
              label:
                "- " +
                this.$t("family.settings.addresses.modal.country_select") +
                " -",
            },
            ...options,
          ];
          console.log("Country options loaded:", this.countryOptions);
        })
        .catch((error) => {
          console.error("Error loading country options:", error);
        });
    },

    getPostalCodeOptions: debounce((search, loading, countryId, vm) => {
      getPostalCodeOptions({ countryId, search }).then((options) => {
        loading(false);
        vm.postalCodeOptions = [...options];
      });
    }, 350),

    fetchPostalCodeOptions(search, loading) {
      const countryId = this.form.country;
      if (countryId && search.length) {
        loading(true);
        this.getPostalCodeOptions(search, loading, countryId, this);
      }
    },

    async onSubmit() {
      console.log("Submitting form:", this.form);
      // Attempt geolocation before submitting
      const requestParams = {
        ...this.prepareRequestParams(this.form),
      };

      if (this.form.latitude && this.form.longitude) {
        console.log("Submitting form with coordinates:", this.form);
        if (this.isEdit) {
          this.updateAddress();
        } else {
          this.addAddress();
        }
        return;
      }

      const geocodedAddress = await this.geocodeUpdatedAddress(requestParams);
      if (geocodedAddress) {
        console.log("geocoding successful:", this.form);
        this.geoCodingConfirm = true;
        this.geocodingFailed = false;
        this.form.latitude = geocodedAddress.lat;
        this.form.longitude = geocodedAddress.lng;
      } else {
        console.log("geocoding failed:", this.form);
        this.geocodingFailed = true;
        this.geoCodingConfirm = false;
      }
    },

    prepareRequestParams(formData) {
      const fields = [
        "id",
        "to",
        "address",
        "city",
        "country",
        "default_ship",
        "default_bill",
        "latitude",
        "longitude",
      ];

      return {
        ...pick(formData, fields),
        postal_code: get(formData.postal_code, "id", ""),
      };
    },

    async addAddress() {
      const requestParams = {
        ...this.prepareRequestParams(this.form),
      };

      const response = await apiPost("auth_faddress", requestParams, [
        this.$store.state.connections.current.family.id,
      ]);

      if (response && response.status === 201) {
        this.$store.dispatch("toaster/add", {
          title: "OK!",
          text: this.$t("family.settings.addresses.actions.msg_add_ok"),
          color: "success",
          autohide: true,
        });

        if (!this.$store.state.connections.current.zip_code) {
          this.$store.state.connections.current.zip_code =
            response.data.payload.postal_code.code;
          await this.saveFamilyName(this.form.to);
        }

        this.closeModal();
        EventBus.$emit("address:refresh");
      } else if (response && response.errors) {
        this.error.set(response.errors);
      } else {
        console.error("Unexpected response:", response);
      }
    },

    async updateAddress() {
      const requestParams = {
        ...this.prepareRequestParams(this.form),
      };

      const response = await apiPatch(
        "auth_faddress",
        requestParams.id,
        requestParams,
        [this.$store.state.connections.current.family.id]
      );

      if (response && response.status === 200) {
        this.$store.dispatch("toaster/add", {
          title: "OK!",
          text: this.$t("family.settings.addresses.actions.msg_edit_ok"),
          color: "success",
          autohide: true,
        });

        this.closeModal();
        EventBus.$emit("address:refresh");
      } else if (response && response.errors) {
        this.error.set(response.errors);
      } else {
        console.error("Unexpected response:", response);
      }
    },

    async saveFamilyName(name) {
      const requestParams = {
        id: this.$store.state.connections.current.family.id,
        name: name,
      };

      const response = await apiPatch(
        "auth_family",
        this.$store.state.connections.current.family.id,
        requestParams,
        []
      );

      if (response && response.status === 200) {
        this.$store.dispatch("connections/currentRefresh");
      } else {
        this.$store.dispatch("toaster/add", {
          title: "Error!",
          text: "Encountered an error",
          color: "error",
          autohide: true,
        });
      }
    },

    async geocodeUpdatedAddress(address) {
      const geocodedAddress = await geocodeAddress(address);
      return geocodedAddress;
    },

    closeModal() {
      this.$emit("update:show", false);
    },

    onFormChange() {
      this.geoCodingConfirm = false;
      this.geocodingFailed = false;
      this.form.latitude = null;
      this.form.longitude = null;
    },

    setupMapClickHandler() {
      const mapObject = this.$refs.map.mapObject;

      mapObject.on("click", (e) => {
        const { lat, lng } = e.latlng;

        this.form.latitude = lat;
        this.form.longitude = lng;
        console.log("Selected coordinates:", lat, lng);

        if (this.marker) {
          mapObject.removeLayer(this.marker);
        }

        this.marker = L.marker([lat, lng], {
          icon: this.customMarkerIcon,
        }).addTo(mapObject);
      });
    },
  },
};
</script>
